<template>
  <transition name="kn-palette-fade">
    <div v-if="visible" class="kn-palette" @mousedown.self="close">
      <div class="kn-palette__panel" role="dialog" aria-label="Поиск по разделам">
        <div class="kn-palette__search">
          <i class="fas fa-search"></i>
          <input
            ref="input"
            v-model="query"
            type="text"
            placeholder="Куда перейти?"
            @keydown.down.prevent="move(1)"
            @keydown.up.prevent="move(-1)"
            @keydown.enter.prevent="go(filtered[activeIndex])"
            @keydown.esc.prevent="close"
          />
          <kbd>Esc</kbd>
        </div>

        <ul v-if="filtered.length" class="kn-palette__list">
          <li
            v-for="(item, index) in filtered"
            :key="item.href"
            class="kn-palette__item"
            :class="{'is-active': index === activeIndex}"
            @mouseenter="activeIndex = index"
            @click="go(item)"
          >
            <span class="kn-palette__item-icon"><i :class="item.icon || 'far fa-file'"></i></span>
            <span class="kn-palette__item-label">{{ item.label }}</span>
            <span v-if="item.group" class="kn-palette__item-group">{{ item.group }}</span>
          </li>
        </ul>
        <div v-else class="kn-palette__empty">Ничего не найдено</div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "command-palette",
  props: {
    // Пункты собираются из отрисованного меню: так учитываются права и клиентский шаблон меню
    menuRoot: {type: Function, required: true},
  },
  data() {
    return {
      visible: false,
      query: '',
      items: [],
      activeIndex: 0,
    }
  },
  computed: {
    filtered() {
      const q = this.query.trim().toLowerCase();
      if (!q) return this.items;
      return this.items.filter(item =>
        item.label.toLowerCase().includes(q) || (item.group || '').toLowerCase().includes(q)
      );
    },
  },
  watch: {
    query() {
      this.activeIndex = 0;
    },
  },
  mounted() {
    window.addEventListener('keydown', this.onGlobalKeydown);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onGlobalKeydown);
  },
  methods: {
    onGlobalKeydown(event) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        this.visible ? this.close() : this.open();
      }
    },
    open() {
      this.items = this.collectItems();
      this.query = '';
      this.activeIndex = 0;
      this.visible = true;
      this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
    },
    close() {
      this.visible = false;
    },
    move(step) {
      if (!this.filtered.length) return;
      this.activeIndex = (this.activeIndex + step + this.filtered.length) % this.filtered.length;
    },
    go(item) {
      if (!item) return;
      this.close();
      if (this.$route.fullPath !== item.href) {
        this.$router.push(item.href).catch(() => {});
      }
    },
    collectItems() {
      const root = this.menuRoot();
      if (!root) return [];

      const isHidden = (el) => {
        for (let node = el; node && node !== document.body; node = node.parentElement) {
          if (node.tagName === 'LI' && node.style.display === 'none') return true;
        }
        return false;
      };

      // В свернутом меню Element переносит подменю во всплывающие слои в body
      const links = [
        ...root.querySelectorAll('a.menu-router-link'),
        ...document.querySelectorAll('body > .el-menu--vertical a.menu-router-link'),
      ];

      const seen = new Set();
      const result = [];
      links.forEach(link => {
        const href = link.getAttribute('href');
        if (!href || seen.has(href) || isHidden(link)) return;
        seen.add(href);

        const submenu = link.closest('li.el-submenu');
        const groupTitle = submenu ? submenu.querySelector('.el-submenu__title') : null;
        const groupIcon = groupTitle ? groupTitle.querySelector('.menu-router-link-icon') : null;
        const ownIcon = link.querySelector('.menu-router-link-icon');
        const textEl = link.querySelector('.menu-router-link-text') || link;

        result.push({
          href,
          label: textEl.textContent.trim(),
          group: groupTitle ? groupTitle.textContent.trim() : null,
          icon: (ownIcon || groupIcon) ? (ownIcon || groupIcon).className.replace('menu-router-link-icon', '').trim() : null,
        });
      });
      return result;
    },
  },
}
</script>

<style lang="scss">
.kn-palette {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 12vh 16px 16px;
  background-color: var(--kn-overlay);
  backdrop-filter: blur(2px);

  &__panel {
    width: 100%;
    max-width: 560px;
    overflow: hidden;
    border: 1px solid var(--kn-border);
    border-radius: var(--kn-radius-lg);
    background-color: var(--kn-card);
    box-shadow: var(--kn-shadow-modal);
  }

  &__search {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 18px;
    height: 56px;
    border-bottom: 1px solid var(--kn-border);
    color: var(--kn-text-faint);

    input {
      flex: 1;
      height: 100%;
      border: 0;
      outline: none;
      background: transparent;
      color: var(--kn-text);
      font-size: 16px;
      font-family: inherit;

      &::placeholder { color: var(--kn-text-faint); }
    }

    kbd {
      padding: 2px 8px;
      border: 1px solid var(--kn-border);
      border-radius: 6px;
      background-color: var(--kn-card-muted);
      font-family: inherit;
      font-size: 11px;
      color: var(--kn-text-muted);
    }
  }

  &__list {
    max-height: 50vh;
    margin: 0;
    padding: 8px;
    overflow-y: auto;
    list-style: none;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: var(--kn-radius-btn);
    color: var(--kn-text);
    cursor: pointer;

    &.is-active {
      background-color: var(--kn-primary-soft);
      color: var(--kn-primary);
    }
  }

  &__item-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 10px;
    background-color: var(--kn-card-muted);
    color: var(--kn-primary);
    flex-shrink: 0;
  }

  &__item-label { flex: 1; font-weight: 500; }

  &__item-group { font-size: 12px; color: var(--kn-text-muted); }

  &__empty {
    padding: 32px;
    text-align: center;
    color: var(--kn-text-muted);
  }
}

.kn-palette-fade-enter-active, .kn-palette-fade-leave-active { transition: opacity .15s ease; }
.kn-palette-fade-enter, .kn-palette-fade-leave-to { opacity: 0; }
</style>
